import { createAppRequest } from '@/services/request';
import type { ApiRequestOptions } from './types/common';

const request = createAppRequest();

export interface ReceiveApplyRecord {
  uuid: string;
  applyNo: string;
  applicantMemberUuid: string;
  departmentUuid?: string;
  warehouseUuid: string;
  applySource: string;
  urgencyLevel: string;
  expectedReceiveAt?: string;
  purpose: string;
  remark?: string;
  status: string;
  ruleResult?: string;
  ruleExplanation?: string;
  riskScore: string;
  riskLevel: string;
  estimatedAmount: string;
  issuedAmount: string;
  rejectReason?: string;
  submittedAt?: string;
  withdrawnAt?: string;
  finishedAt?: string;
  createdAt: string;
}

export interface ReceiveApplyItemRecord {
  uuid: string;
  applyUuid: string;
  materialUuid: string;
  warehouseUuid: string;
  materialCodeSnapshot?: string;
  materialNameSnapshot?: string;
  quantity: string;
  availableQtySnapshot: string;
  unitCost: string;
  estimatedAmount: string;
  issuedQty: string;
  pendingQty: string;
  issuedAmount: string;
  ruleHitReason?: string;
}

export interface IssueRecordRecord {
  uuid: string;
  issueNo: string;
  applyUuid: string;
  applyItemUuid: string;
  materialUuid: string;
  warehouseUuid: string;
  issueQty: string;
  unitCost: string;
  issueAmount: string;
  issuerMemberUuid?: string;
  receiverMemberUuid: string;
  stockFlowNo?: string;
  remark?: string;
  issuedAt: string;
}

export interface ReceiveReturnOrderRecord {
  uuid: string;
  returnNo: string;
  applyUuid: string;
  applyNo?: string;
  warehouseUuid: string;
  returnerMemberUuid: string;
  receiverMemberUuid?: string;
  returnReason: string;
  status: string;
  totalReturnQty: string;
  totalAcceptedQty: string;
  totalRejectedQty: string;
  totalReturnAmount: string;
  remark?: string;
  rejectReason?: string;
  submittedAt?: string;
  confirmedAt?: string;
  cancelledAt?: string;
  createdAt: string;
}

export interface ReceiveReturnItemRecord {
  uuid: string;
  returnUuid: string;
  issueRecordUuid?: string;
  applyUuid: string;
  applyItemUuid?: string;
  warehouseUuid: string;
  materialUuid: string;
  materialCodeSnapshot?: string;
  materialNameSnapshot?: string;
  issuedQtySnapshot: string;
  returnedQtySnapshot: string;
  returnQty: string;
  acceptedQty: string;
  rejectedQty: string;
  unitCost: string;
  returnAmount: string;
  qualityStatus: string;
  stockFlowNo?: string;
  remark?: string;
}

export interface ReceiveReturnDetailRecord {
  order: ReceiveReturnOrderRecord;
  items: ReceiveReturnItemRecord[];
}

export interface ReceiveApplyDetailRecord {
  apply: ReceiveApplyRecord;
  items: ReceiveApplyItemRecord[];
  issueRecords: IssueRecordRecord[];
}

export interface CreateReceiveApplyPayload {
  warehouseUuid: string;
  departmentUuid?: string;
  purpose: string;
  applySource?: 'manual' | 'reapply' | 'frequent' | 'ai_recommend';
  urgencyLevel?: 'normal' | 'urgent' | 'emergency';
  expectedReceiveAt?: string;
  remark?: string;
  items: Array<{
    materialUuid: string;
    quantity: number;
  }>;
}

export type UpdateReceiveApplyPayload = Partial<CreateReceiveApplyPayload>;

export interface IssueReceiveApplyPayload {
  remark?: string;
  items?: Array<{
    materialUuid: string;
    quantity: number;
  }>;
}

export interface CreateReceiveReturnPayload {
  applyUuid: string;
  returnReason?: 'unused' | 'wrong_issue' | 'employee_leave' | 'damaged' | 'other';
  remark?: string;
  items: Array<{
    issueRecordUuid?: string;
    materialUuid: string;
    returnQty: number;
    qualityStatus?: 'normal' | 'damaged' | 'unusable';
    remark?: string;
  }>;
}

export interface ConfirmReceiveReturnPayload {
  remark?: string;
  items?: Array<{
    returnItemUuid: string;
    acceptedQty: number;
    rejectedQty?: number;
    remark?: string;
  }>;
}

export interface ReceiveRuleCheckResult {
  finalDecision: string;
  explanation: string;
  riskScore: number;
  riskLevel: string;
  estimatedAmount: number;
  items: Array<{
    materialUuid: string;
    materialName: string;
    quantity: number;
    availableQty: number;
    unitCost: number;
    estimatedAmount: number;
    ruleHitReason?: string;
  }>;
}

export async function getReceiveApplies(options?: ApiRequestOptions) {
  return request<ReceiveApplyRecord[]>('/bff/receive/applies', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getReceiveApplyDetail(
  applyUuid: string,
  options?: ApiRequestOptions,
) {
  return request<ReceiveApplyDetailRecord>(`/bff/receive/applies/${applyUuid}/detail`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getMyReceiveApplies(options?: ApiRequestOptions) {
  return request<ReceiveApplyRecord[]>('/bff/receive/applies/my', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getReceiveApplyItems(
  applyUuid: string,
  options?: ApiRequestOptions,
) {
  return request<ReceiveApplyItemRecord[]>(`/bff/receive/applies/${applyUuid}/items`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getReceiveApplyItemsBatch(
  applyUuids: string[],
  options?: ApiRequestOptions,
) {
  return request<ReceiveApplyItemRecord[]>('/bff/receive/applies/items/batch', {
    method: 'POST',
    data: { applyUuids },
    ...(options || {}),
  });
}

export async function getReceiveIssueRecords(
  applyUuid: string,
  options?: ApiRequestOptions,
) {
  return request<IssueRecordRecord[]>(
    `/bff/receive/applies/${applyUuid}/issue-records`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

export async function createReceiveApply(
  data: CreateReceiveApplyPayload,
  options?: ApiRequestOptions,
) {
  return request<ReceiveApplyRecord>('/bff/receive/applies', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function createReceiveApplyDraft(
  data: CreateReceiveApplyPayload,
  options?: ApiRequestOptions,
) {
  return request<ReceiveApplyRecord>('/bff/receive/applies/drafts', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function updateReceiveApply(
  applyUuid: string,
  data: UpdateReceiveApplyPayload,
  options?: ApiRequestOptions,
) {
  return request<ReceiveApplyRecord>(`/bff/receive/applies/${applyUuid}`, {
    method: 'PUT',
    data,
    ...(options || {}),
  });
}

export async function submitReceiveApply(
  applyUuid: string,
  options?: ApiRequestOptions,
) {
  return request<ReceiveApplyRecord>(`/bff/receive/applies/${applyUuid}/submit`, {
    method: 'POST',
    ...(options || {}),
  });
}

export async function copyReceiveApply(
  applyUuid: string,
  options?: ApiRequestOptions,
) {
  return request<ReceiveApplyRecord>(`/bff/receive/applies/${applyUuid}/copy`, {
    method: 'POST',
    ...(options || {}),
  });
}

export async function cancelReceiveApply(
  applyUuid: string,
  data: { reason?: string } = {},
  options?: ApiRequestOptions,
) {
  return request<ReceiveApplyRecord>(`/bff/receive/applies/${applyUuid}`, {
    method: 'DELETE',
    data,
    ...(options || {}),
  });
}

export async function checkReceiveRules(
  data: CreateReceiveApplyPayload,
  options?: ApiRequestOptions,
) {
  return request<ReceiveRuleCheckResult>('/bff/receive/applies/check', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function issueReceiveApply(
  applyUuid: string,
  data: IssueReceiveApplyPayload = {},
  options?: ApiRequestOptions,
) {
  return request<ReceiveApplyRecord>(`/bff/receive/applies/${applyUuid}/issue`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function withdrawReceiveApply(
  applyUuid: string,
  data: { reason?: string } = {},
  options?: ApiRequestOptions,
) {
  return request<ReceiveApplyRecord>(`/bff/receive/applies/${applyUuid}/withdraw`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function approveReceiveApply(
  applyUuid: string,
  data: { comment?: string } = {},
  options?: ApiRequestOptions,
) {
  return request<ReceiveApplyRecord>(`/bff/receive/applies/${applyUuid}/approve`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function rejectReceiveApply(
  applyUuid: string,
  data: { comment?: string } = {},
  options?: ApiRequestOptions,
) {
  return request<ReceiveApplyRecord>(`/bff/receive/applies/${applyUuid}/reject`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function getReceiveReturns(options?: ApiRequestOptions) {
  return request<ReceiveReturnOrderRecord[]>('/bff/receive/returns', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getReceiveReturnDetail(
  returnUuid: string,
  options?: ApiRequestOptions,
) {
  return request<ReceiveReturnDetailRecord>(`/bff/receive/returns/${returnUuid}`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createReceiveReturn(
  data: CreateReceiveReturnPayload,
  options?: ApiRequestOptions,
) {
  return request<ReceiveReturnOrderRecord>('/bff/receive/returns', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function confirmReceiveReturn(
  returnUuid: string,
  data: ConfirmReceiveReturnPayload = {},
  options?: ApiRequestOptions,
) {
  return request<ReceiveReturnOrderRecord>(`/bff/receive/returns/${returnUuid}/confirm`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function rejectReceiveReturn(
  returnUuid: string,
  data: { reason?: string } = {},
  options?: ApiRequestOptions,
) {
  return request<ReceiveReturnOrderRecord>(`/bff/receive/returns/${returnUuid}/reject`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function cancelReceiveReturn(
  returnUuid: string,
  data: { reason?: string } = {},
  options?: ApiRequestOptions,
) {
  return request<ReceiveReturnOrderRecord>(`/bff/receive/returns/${returnUuid}/cancel`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}
